import { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import {
  EligibleWorkflowsAssigneesDocument,
  WorkflowTeamSelectorDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { type ManualApproveHandlerSchema } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/manualApproveHandler'
import { HandlerFormComponent } from 'modules/workflows/contexts/WorkflowContext'
import {
  type DurationUnit,
  getDurationUnitOptions,
  isValidDateUnit,
} from 'modules/workflows/lib/durationUnit'

import { ApproverSelectionContainer } from './ApproverSelectionContainer'
import {
  type StrategyItem,
  parseStrategiesFromPayload,
  strategiesToPayload,
} from './manualApproveHandlerUtils'

const DEFAULT_DURATION_UNIT = 'days'

export const RequestReviewHandlerForm = ({}: HandlerFormComponent) => {
  const { company } = useCurrentSession()
  const { control, watch, setValue, getValues } = useFormContext<ManualApproveHandlerSchema>()

  const { loading: employeesLoading, data: employeesData } = useQuery(
    EligibleWorkflowsAssigneesDocument,
    {
      variables: {
        companyId: company.id,
        active: true,
      },
    }
  )
  const { loading: teamsLoading, data: teamsData } = useQuery(WorkflowTeamSelectorDocument)

  const payload = watch('nodeData.payload')
  const dueOnValue = payload?.due_on_value
  const dueOnUnit = payload?.due_on_unit ?? DEFAULT_DURATION_UNIT

  const strategies = useMemo(() => {
    if (!payload) {
      return []
    }
    return parseStrategiesFromPayload(payload)
  }, [payload])

  const availableTeams = teamsData?.teams?.teamsConnection?.nodes || []
  const availableEmployees = employeesData?.procurement?.eligibleWorkflowsAssignees || []

  const handleAddStrategies = (newStrategies: StrategyItem[]) => {
    if (newStrategies.length === 0) {
      return
    }

    const currentFormValues = getValues()
    const currentPayload = currentFormValues.nodeData?.payload

    const due_on_value = currentPayload?.due_on_value ?? 1
    const due_on_unit_raw = currentPayload?.due_on_unit ?? DEFAULT_DURATION_UNIT
    const due_on_unit: DurationUnit = isValidDateUnit(due_on_unit_raw)
      ? due_on_unit_raw
      : DEFAULT_DURATION_UNIT

    const currentStrategies = currentPayload ? parseStrategiesFromPayload(currentPayload) : []
    const allStrategies = [...currentStrategies, ...newStrategies]

    const newPayload = strategiesToPayload(allStrategies, due_on_value, due_on_unit)
    setValue('nodeData.payload', newPayload, { shouldValidate: true, shouldDirty: true })
  }

  const handleDeleteStrategy = (strategyId: string) => {
    const currentFormValues = getValues()
    const currentPayload = currentFormValues.nodeData.payload || {
      due_on_value: 1,
      due_on_unit: DEFAULT_DURATION_UNIT,
    }
    const due_on_value = currentPayload.due_on_value ?? 1
    const due_on_unit_raw = currentPayload.due_on_unit ?? DEFAULT_DURATION_UNIT
    const due_on_unit: DurationUnit = isValidDateUnit(due_on_unit_raw)
      ? due_on_unit_raw
      : DEFAULT_DURATION_UNIT

    const currentStrategies = parseStrategiesFromPayload(currentPayload)
    const updatedStrategies = currentStrategies.filter((s) => s.id !== strategyId)

    if (updatedStrategies.length === 0) {
      return
    }

    const newPayload = strategiesToPayload(updatedStrategies, due_on_value, due_on_unit)
    setValue('nodeData.payload', newPayload, { shouldValidate: true, shouldDirty: true })
  }

  return (
    <Box gap="s16">
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.approval.fields.due_on')}
        </Text>
        <Box flexDirection="row" gap="s8" width="full" alignItems="flexEnd">
          <Box style={{ width: '100px' }}>
            <Controller
              name="nodeData.payload.due_on_value"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  size="md"
                  label="Duration value"
                  hideLabel
                  placeholder="2"
                  value={String(field.value ?? '')}
                  onChange={(value) => field.onChange(Number(value))}
                  type="number"
                  error={fieldState.error?.message}
                />
              )}
            />
          </Box>
          <Box width="full">
            <Controller
              name="nodeData.payload.due_on_unit"
              control={control}
              render={({ field, fieldState }) => (
                <F0Select
                  size="md"
                  label="Duration unit"
                  hideLabel
                  value={field.value ?? DEFAULT_DURATION_UNIT}
                  onChange={field.onChange}
                  options={getDurationUnitOptions()}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Box>
        </Box>
        <Text size="100" color="textSecondary">
          {dueOnValue && dueOnUnit
            ? i18n.t('workflows.sidepanel.approval.due_date_explanation.with_values', {
                value: dueOnValue,
                unit: dueOnUnit,
              })
            : i18n.t('workflows.sidepanel.approval.due_date_explanation.default')}
        </Text>
      </Box>

      <ApproverSelectionContainer
        strategies={strategies}
        onAddStrategies={handleAddStrategies}
        onDeleteStrategy={handleDeleteStrategy}
        availableEmployees={availableEmployees}
        availableTeams={availableTeams}
        employeesLoading={employeesLoading}
        teamsLoading={teamsLoading}
      />
    </Box>
  )
}
